import { z } from 'zod';
import {
  AiUsageLedgerDimensionsSchema,
  type AiUsageLedgerDimensions,
  type AiUsageLedgerDimensionsInput,
} from './ai-usage';

export const AI_LOG_SCHEMA_VERSION = 1 as const;
export const AI_USAGE_DIMENSIONS_SCHEMA_VERSION = 1 as const;
export const AI_LOG_DETAILS_MAX_BYTES = 16 * 1024;

export function tenantScopedAttemptId(tenantId: string, attemptId: string): string {
  const identity = `${tenantId}\u001f${attemptId}`;
  const hex = [0x811c9dc5, 0x9e3779b9, 0x85ebca6b, 0xc2b2ae35]
    .map((seed) => stableHash32(identity, seed).toString(16).padStart(8, '0'))
    .join('');
  return `${attemptId}~${hex}`;
}

const safeNonNegativeInteger = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const nullableSafeNonNegativeInteger = safeNonNegativeInteger.nullable();
const boundedIdentity = z.string().trim().min(1).max(200);
const nullableBoundedIdentity = boundedIdentity.nullable();
const aiLogTimestamp = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .refine((value) => !Number.isNaN(Date.parse(value)), 'timestamp must be parseable');

const forbiddenDetailKey =
  /(^|_)(prompt|response|content|body|headers?|authorization|credentials?|secrets?|api_key|access_token|refresh_token)($|_)/;

const AiLogDetailAttributeValueSchema = z.union([
  z.string().max(1000),
  safeNonNegativeInteger,
  z.boolean(),
  z.null(),
]);

export const AiLogDetailAttributesSchema = z
  .record(z.string().trim().min(1).max(80), AiLogDetailAttributeValueSchema)
  .superRefine((attributes, context) => {
    const keys = Object.keys(attributes);
    if (keys.length > 50) {
      context.addIssue({
        code: 'custom',
        message: 'AI log details may contain at most 50 attributes',
      });
    }
    for (const key of keys) {
      const normalized = normalizeAiLogDetailKey(key);
      if (forbiddenDetailKey.test(normalized)) {
        context.addIssue({
          code: 'custom',
          path: [key],
          message: 'AI log details must not contain sensitive payload fields',
        });
      }
    }
  });

const AiLogModelAttemptDetailsSchema = z
  .object({
    event_type: z.literal('model_attempt'),
    schema_version: z.literal(AI_LOG_SCHEMA_VERSION),
    source_result: z.enum([
      'provider_succeeded',
      'provider_failed',
      'output_validation_failed',
      'business_validation_failed',
    ]),
    internal_call: z.boolean(),
    attributes: AiLogDetailAttributesSchema.default({}),
  })
  .strict();

const AiLogToolCallDetailsSchema = z
  .object({
    event_type: z.literal('tool_call'),
    schema_version: z.literal(AI_LOG_SCHEMA_VERSION),
    tool_type: z.string().trim().min(1).max(120),
    source_result: z.enum(['succeeded', 'failed']),
    cost_incurred: z.boolean(),
    attributes: AiLogDetailAttributesSchema.default({}),
  })
  .strict();

const AiLogCacheEventDetailsSchema = z
  .object({
    event_type: z.literal('cache_event'),
    schema_version: z.literal(AI_LOG_SCHEMA_VERSION),
    cache_name: z.literal('chat_config'),
    operation: z.enum(['read', 'inspect', 'rebuild']),
    outcome: z.enum(['hit', 'miss', 'succeeded', 'failed']),
    attributes: AiLogDetailAttributesSchema.default({}),
  })
  .strict();

const AiLogFinalMessageDetailsSchema = z
  .object({
    event_type: z.literal('final_message'),
    schema_version: z.literal(AI_LOG_SCHEMA_VERSION),
    role: z.literal('assistant'),
    status: z.enum(['processing', 'succeeded', 'failed']),
    attributes: AiLogDetailAttributesSchema.default({}),
  })
  .strict();

const AiLogReservationDetailsSchema = z
  .object({
    event_type: z.literal('reservation'),
    schema_version: z.literal(AI_LOG_SCHEMA_VERSION),
    status: z.enum(['reserved', 'settled', 'refunded']),
    reserved_points: safeNonNegativeInteger,
    settled_points: nullableSafeNonNegativeInteger,
    released_points: safeNonNegativeInteger,
    shortfall_points: nullableSafeNonNegativeInteger,
    failure_reason: z.string().trim().min(1).max(120).nullable(),
    attributes: AiLogDetailAttributesSchema.default({}),
  })
  .strict();

export const AiLogEventDetailsSchema = z
  .discriminatedUnion('event_type', [
    AiLogModelAttemptDetailsSchema,
    AiLogToolCallDetailsSchema,
    AiLogCacheEventDetailsSchema,
    AiLogFinalMessageDetailsSchema,
    AiLogReservationDetailsSchema,
  ])
  .superRefine((details, context) => {
    if (utf8ByteLength(JSON.stringify(details)) > AI_LOG_DETAILS_MAX_BYTES) {
      context.addIssue({
        code: 'custom',
        message: `AI log details exceed ${AI_LOG_DETAILS_MAX_BYTES} bytes`,
      });
    }
  });

export type AiLogEventDetails = z.output<typeof AiLogEventDetailsSchema>;
export type AiModelAttemptLogDetails = Extract<AiLogEventDetails, { event_type: 'model_attempt' }>;

export function parseAiLogEventDetailsJson(value: string): AiLogEventDetails {
  assertAiLogDetailsSize(value);
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch (error) {
    throw new Error('AI log details JSON is invalid', { cause: error });
  }
  return AiLogEventDetailsSchema.parse(parsed);
}

export function serializeAiLogEventDetails(value: unknown): string {
  const serialized = JSON.stringify(AiLogEventDetailsSchema.parse(value));
  assertAiLogDetailsSize(serialized);
  return serialized;
}

export function parseAiUsageLedgerDimensionsJson(
  value: string,
  schemaVersion: unknown,
): AiUsageLedgerDimensions {
  z.literal(AI_USAGE_DIMENSIONS_SCHEMA_VERSION).parse(schemaVersion);
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch (error) {
    throw new Error('AI usage dimensions JSON is invalid', { cause: error });
  }
  return AiUsageLedgerDimensionsSchema.parse(parsed);
}

export function serializeAiUsageLedgerDimensions(value: AiUsageLedgerDimensionsInput): {
  schema_version: typeof AI_USAGE_DIMENSIONS_SCHEMA_VERSION;
  json: string;
} {
  return {
    schema_version: AI_USAGE_DIMENSIONS_SCHEMA_VERSION,
    json: JSON.stringify(AiUsageLedgerDimensionsSchema.parse(value)),
  };
}

function assertAiLogDetailsSize(value: string): void {
  if (utf8ByteLength(value) > AI_LOG_DETAILS_MAX_BYTES) {
    throw new Error(`AI log details exceed ${AI_LOG_DETAILS_MAX_BYTES} bytes`);
  }
}

function utf8ByteLength(value: string): number {
  let bytes = 0;
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    bytes += codePoint <= 0x7f ? 1 : codePoint <= 0x7ff ? 2 : codePoint <= 0xffff ? 3 : 4;
  }
  return bytes;
}

export const AiLogEventTypeSchema = z.enum([
  'model_attempt',
  'tool_call',
  'cache_event',
  'final_message',
  'reservation',
]);

export const AiLogListCursorSchema = z
  .object({
    version: z.literal(AI_LOG_SCHEMA_VERSION),
    tuple: z.tuple([
      aiLogTimestamp,
      z.string().trim().min(1).max(80),
      AiLogEventTypeSchema,
      boundedIdentity,
    ]),
  })
  .strict();
export type AiLogListCursor = z.output<typeof AiLogListCursorSchema>;

export const AiLogAuthoritySchema = z.enum([
  'ai_usage_ledger',
  'ai_model_call_events',
  'ai_tool_call_events',
  'chat_config_cache_event',
  'ai_chat_messages',
  'ai_point_reservations',
]);

export const AiLogUsageSchema = z
  .object({
    prompt_tokens: nullableSafeNonNegativeInteger,
    completion_tokens: nullableSafeNonNegativeInteger,
    cache_read_tokens: nullableSafeNonNegativeInteger,
    cache_write_tokens: nullableSafeNonNegativeInteger,
    reasoning_tokens: nullableSafeNonNegativeInteger,
    total_tokens: nullableSafeNonNegativeInteger,
    output_units: nullableSafeNonNegativeInteger,
    input_tokens: nullableSafeNonNegativeInteger,
    output_tokens: nullableSafeNonNegativeInteger,
    cached_tokens: nullableSafeNonNegativeInteger,
    image_input_units: nullableSafeNonNegativeInteger,
    image_output_units: nullableSafeNonNegativeInteger,
    audio_input_units: nullableSafeNonNegativeInteger,
    audio_output_units: nullableSafeNonNegativeInteger,
    tool_calls: nullableSafeNonNegativeInteger,
  })
  .strict();

export const AiLogCostSchema = z
  .object({
    input_cost_per_1m_cents: nullableSafeNonNegativeInteger,
    output_cost_per_1m_cents: nullableSafeNonNegativeInteger,
    actual_cost_micros: nullableSafeNonNegativeInteger,
    currency: z.string().trim().length(3).nullable(),
    billing_owner: z.enum(['user_points', 'platform']).nullable(),
    fixed_points: nullableSafeNonNegativeInteger,
  })
  .strict();

export const AiLogEventSchema = z
  .object({
    schema_version: z.literal(AI_LOG_SCHEMA_VERSION),
    event_id: boundedIdentity,
    event_type: AiLogEventTypeSchema,
    authority: AiLogAuthoritySchema,
    tenant_id: z.string().trim().min(1).max(80),
    user_id: z.string().trim().min(1).max(80).nullable(),
    occurred_at: aiLogTimestamp,
    execution_id: nullableBoundedIdentity,
    call_id: nullableBoundedIdentity,
    attempt_number: z.number().int().positive().max(Number.MAX_SAFE_INTEGER).nullable(),
    action_type: z.string().trim().min(1).max(80).nullable(),
    action_id: nullableBoundedIdentity,
    message_id: nullableBoundedIdentity,
    reservation_id: z.string().trim().min(1).max(80).nullable(),
    skill_id: z.string().trim().min(1).max(128).nullable(),
    tool_call_id: nullableBoundedIdentity,
    tool_name: z.string().trim().min(1).max(120).nullable(),
    model: z
      .object({
        product_model_id: z.string().trim().min(1).max(120),
        runtime_provider: z.string().trim().min(1).max(80).nullable(),
        runtime_model_id: z.string().trim().min(1).max(240).nullable(),
        provider_request_id: z.string().trim().min(1).max(240).nullable(),
      })
      .strict()
      .nullable(),
    provider_result: z.enum(['succeeded', 'failed']).nullable(),
    business_result: z.enum(['pending', 'succeeded', 'failed']).nullable(),
    error: z
      .object({
        category: z.string().trim().min(1).max(80).nullable(),
        code: z.string().trim().min(1).max(120).nullable(),
        message: z.string().max(1000).nullable(),
      })
      .strict()
      .nullable(),
    usage: AiLogUsageSchema.nullable(),
    cost: AiLogCostSchema.nullable(),
    quality: z
      .object({
        event_id: boundedIdentity,
        occurred_at: aiLogTimestamp,
        result: z.enum(['passed', 'failed', 'pending', 'not_evaluated']),
      })
      .strict()
      .nullable(),
    details: AiLogEventDetailsSchema,
  })
  .strict()
  .superRefine((event, context) => {
    if (event.event_type !== event.details.event_type) {
      context.addIssue({
        code: 'custom',
        path: ['details', 'event_type'],
        message: 'details event_type must match the event envelope',
      });
    }
    const expectedAuthority: Record<z.output<typeof AiLogEventTypeSchema>, readonly string[]> = {
      model_attempt: ['ai_usage_ledger', 'ai_model_call_events'],
      tool_call: ['ai_tool_call_events'],
      cache_event: ['chat_config_cache_event'],
      final_message: ['ai_chat_messages'],
      reservation: ['ai_point_reservations'],
    };
    if (!expectedAuthority[event.event_type].includes(event.authority)) {
      context.addIssue({
        code: 'custom',
        path: ['authority'],
        message: 'authority does not own this AI log event type',
      });
    }
    const requiredFields: Partial<
      Record<z.output<typeof AiLogEventTypeSchema>, Array<keyof typeof event>>
    > = {
      model_attempt: ['execution_id', 'call_id', 'attempt_number', 'model', 'provider_result'],
      tool_call: [
        'execution_id',
        'attempt_number',
        'message_id',
        'reservation_id',
        'tool_call_id',
        'tool_name',
        'business_result',
      ],
      final_message: ['execution_id', 'message_id', 'business_result'],
      reservation: ['reservation_id'],
    };
    for (const field of requiredFields[event.event_type] ?? []) {
      if (event[field] === null) {
        context.addIssue({
          code: 'custom',
          path: [field],
          message: `${field} is required for ${event.event_type}`,
        });
      }
    }
  });

export type AiLogEvent = z.output<typeof AiLogEventSchema>;

export const AiLogPayloadStatusSchema = z.enum([
  'pending',
  'available',
  'missing',
  'deleted',
  'not_applicable',
]);
export type AiLogPayloadStatus = z.infer<typeof AiLogPayloadStatusSchema>;

export const AiLogCompletenessStatusSchema = z.enum(['complete', 'delayed', 'missing', 'failed']);
export type AiLogCompletenessStatus = z.infer<typeof AiLogCompletenessStatusSchema>;

export const AdminAiLogListItemSchema = AiLogEventSchema.extend({
  payload_status: AiLogPayloadStatusSchema,
});
export type AdminAiLogListItem = z.output<typeof AdminAiLogListItemSchema>;

export const AdminAiLogCompletenessSchema = z
  .object({
    status: z.enum(['complete', 'partial']),
    meta: AiLogCompletenessStatusSchema,
    analytics: AiLogCompletenessStatusSchema,
    shards: z.record(z.string().trim().min(1).max(80), AiLogCompletenessStatusSchema),
  })
  .strict();
export type AdminAiLogCompleteness = z.output<typeof AdminAiLogCompletenessSchema>;

export const AdminAiLogListResponseSchema = z
  .object({
    items: z.array(AdminAiLogListItemSchema),
    next_cursor: z.string().nullable(),
    completeness: AdminAiLogCompletenessSchema,
  })
  .strict();
export type AdminAiLogListResponse = z.output<typeof AdminAiLogListResponseSchema>;

export const AdminAiLogTraceIdTypeSchema = z.enum([
  'business_request',
  'execution',
  'action',
  'message',
  'call',
  'attempt',
  'reservation',
  'provider_request',
  'skill',
  'tool_call',
]);
export type AdminAiLogTraceIdType = z.infer<typeof AdminAiLogTraceIdTypeSchema>;

export const AdminAiLogTraceLinkSchema = z
  .object({
    id_type: AdminAiLogTraceIdTypeSchema,
    id: boundedIdentity,
    event_type: AiLogEventTypeSchema,
    event_id: boundedIdentity,
  })
  .strict();
export type AdminAiLogTraceLink = z.output<typeof AdminAiLogTraceLinkSchema>;

export const AdminAiLogDetailResponseSchema = z
  .object({
    item: AdminAiLogListItemSchema,
    related_ids: z.array(AdminAiLogTraceLinkSchema),
    completeness: AdminAiLogCompletenessSchema,
  })
  .strict();
export type AdminAiLogDetailResponse = z.output<typeof AdminAiLogDetailResponseSchema>;

export const AdminAiLogTraceResponseSchema = z
  .object({
    id_type: AdminAiLogTraceIdTypeSchema,
    id: boundedIdentity,
    items: z.array(AdminAiLogListItemSchema),
    related_ids: z.array(AdminAiLogTraceLinkSchema),
    completeness: AdminAiLogCompletenessSchema,
  })
  .strict();
export type AdminAiLogTraceResponse = z.output<typeof AdminAiLogTraceResponseSchema>;

const sourceBoolean = z.union([z.boolean(), z.literal(0), z.literal(1)]);
const nullableSourceString = z.string().nullable();

export const AiUsageLedgerLogSourceSchema = z
  .object({
    source_kind: z.literal('ai_usage_ledger'),
    attempt_id: boundedIdentity,
    source_event_id: nullableBoundedIdentity,
    source: z.enum(['model_call', 'observability']),
    tenant_id: z.string().trim().min(1).max(80),
    user_id: z.string().trim().min(1).max(80).nullable(),
    action_type: z.string().trim().min(1).max(80).nullable(),
    action_id: nullableBoundedIdentity,
    message_id: nullableBoundedIdentity,
    reservation_id: z.string().trim().min(1).max(80).nullable(),
    execution_id: boundedIdentity,
    call_id: boundedIdentity,
    attempt_no: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    feature_key: z.string().trim().min(1).max(80).nullable(),
    task_type: z.string().trim().min(1).max(80).nullable(),
    model_id: z.string().trim().min(1).max(120),
    result: z.enum([
      'provider_succeeded',
      'provider_failed',
      'output_validation_failed',
      'business_validation_failed',
    ]),
    error_category: z.string().trim().min(1).max(80).nullable(),
    internal_call: sourceBoolean,
    skill_id: z.string().trim().min(1).max(128).nullable(),
    tool_call_id: nullableBoundedIdentity,
    tool_name: z.string().trim().min(1).max(120).nullable(),
    prompt_tokens: nullableSafeNonNegativeInteger,
    completion_tokens: nullableSafeNonNegativeInteger,
    cache_read_tokens: nullableSafeNonNegativeInteger,
    cache_write_tokens: nullableSafeNonNegativeInteger,
    reasoning_tokens: nullableSafeNonNegativeInteger,
    total_tokens: nullableSafeNonNegativeInteger,
    usage_dimensions_schema_version: z.number().int(),
    usage_dimensions_json: z.string(),
    details_json: z.string().nullable().optional().default(null),
    quality_event_id: nullableBoundedIdentity,
    quality_occurred_at: nullableSourceString,
    provider_request_id: z.string().trim().min(1).max(240).nullable(),
    input_cost_per_1m_cents: nullableSafeNonNegativeInteger,
    output_cost_per_1m_cents: nullableSafeNonNegativeInteger,
    actual_cost_micros: nullableSafeNonNegativeInteger,
    currency: z.string().trim().length(3).nullable(),
    billing_owner: z.enum(['user_points', 'platform']).nullable(),
    fixed_points: nullableSafeNonNegativeInteger,
    occurred_at: aiLogTimestamp,
  })
  .strict();

export const AiModelCallLogSourceSchema = z
  .object({
    source_kind: z.literal('ai_model_call_events'),
    id: boundedIdentity,
    tenant_id: z.string().trim().min(1).max(80),
    user_id: z.string().trim().min(1).max(80).nullable(),
    reservation_id: z.string().trim().min(1).max(80).nullable(),
    feature_key: z.string().trim().min(1).max(80),
    ref_type: z.string().trim().min(1).max(80),
    ref_id: boundedIdentity,
    execution_id: nullableBoundedIdentity,
    call_id: nullableBoundedIdentity,
    attempt_no: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    model_id: z.string().trim().min(1).max(120),
    outcome: z.enum(['succeeded', 'failed']),
    result: z
      .enum([
        'provider_succeeded',
        'provider_failed',
        'output_validation_failed',
        'business_validation_failed',
      ])
      .nullable()
      .optional(),
    error_category: z.string().trim().min(1).max(80).nullable(),
    prompt_tokens: nullableSafeNonNegativeInteger,
    completion_tokens: nullableSafeNonNegativeInteger,
    cache_read_tokens: nullableSafeNonNegativeInteger.optional().default(null),
    cache_write_tokens: nullableSafeNonNegativeInteger.optional().default(null),
    reasoning_tokens: nullableSafeNonNegativeInteger.optional().default(null),
    total_tokens: nullableSafeNonNegativeInteger.optional().default(null),
    image_input_units: nullableSafeNonNegativeInteger.optional().default(null),
    image_output_units: nullableSafeNonNegativeInteger.optional().default(null),
    audio_input_units: nullableSafeNonNegativeInteger.optional().default(null),
    audio_output_units: nullableSafeNonNegativeInteger.optional().default(null),
    duration_ms: nullableSafeNonNegativeInteger,
    internal_call: sourceBoolean,
    created_at: aiLogTimestamp,
  })
  .strict();

export const AiToolCallLogSourceSchema = z
  .object({
    source_kind: z.literal('ai_tool_call_events'),
    tenant_id: z.string().trim().min(1).max(80),
    event_id: boundedIdentity,
    reservation_id: z.string().trim().min(1).max(80),
    execution_id: boundedIdentity,
    action_id: boundedIdentity,
    message_id: boundedIdentity,
    skill_id: z.string().trim().min(1).max(128).nullable(),
    tool_call_id: boundedIdentity,
    attempt_number: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    tool_type: z.string().trim().min(1).max(120),
    tool_name: z.string().trim().min(1).max(120),
    result: z.enum(['succeeded', 'failed']),
    cost_incurred: sourceBoolean,
    actual_cost_micros: nullableSafeNonNegativeInteger,
    currency: z.literal('USD').nullable(),
    occurred_at: aiLogTimestamp,
  })
  .strict();

export const AiFinalMessageLogSourceSchema = z
  .object({
    source_kind: z.literal('ai_chat_messages'),
    id: boundedIdentity,
    tenant_id: z.string().trim().min(1).max(80),
    user_id: z.string().trim().min(1).max(80),
    run_id: boundedIdentity,
    role: z.literal('assistant'),
    status: z.enum(['processing', 'succeeded', 'failed']),
    model_id: z.string().trim().min(1).max(120).nullable(),
    reservation_id: z.string().trim().min(1).max(80).nullable(),
    prompt_tokens: nullableSafeNonNegativeInteger,
    completion_tokens: nullableSafeNonNegativeInteger,
    cache_read_tokens: nullableSafeNonNegativeInteger,
    cache_write_tokens: nullableSafeNonNegativeInteger,
    reasoning_tokens: nullableSafeNonNegativeInteger,
    total_tokens: nullableSafeNonNegativeInteger,
    failure_code: z.string().trim().min(1).max(120).nullable(),
    failure_message: z.string().max(1000).nullable(),
    created_at: aiLogTimestamp,
    completed_at: z.string().nullable(),
  })
  .strict();

export const AiReservationLogSourceSchema = z
  .object({
    source_kind: z.literal('ai_point_reservations'),
    id: z.string().trim().min(1).max(80),
    tenant_id: z.string().trim().min(1).max(80),
    user_id: z.string().trim().min(1).max(80).nullable(),
    feature_key: z.string().trim().min(1).max(80),
    ref_type: z.string().trim().min(1).max(80),
    ref_id: boundedIdentity,
    status: z.enum(['reserved', 'settled', 'refunded']),
    reserved_points: safeNonNegativeInteger,
    settled_points: nullableSafeNonNegativeInteger,
    released_points: safeNonNegativeInteger,
    final_model_id: z.string().trim().min(1).max(120).nullable(),
    settlement_shortfall_points: nullableSafeNonNegativeInteger.optional().default(null),
    settlement_failure_reason: z
      .string()
      .trim()
      .min(1)
      .max(120)
      .nullable()
      .optional()
      .default(null),
    refund_reason: z.string().trim().min(1).max(120).nullable(),
    reserved_at: aiLogTimestamp,
    settled_at: z.string().nullable(),
    refunded_at: z.string().nullable(),
  })
  .strict();

export const AiChatConfigCacheLogSourceSchema = z
  .object({
    source_kind: z.literal('chat_config_cache_event'),
    event_id: boundedIdentity,
    tenant_id: z.string().trim().min(1).max(80),
    user_id: z.string().trim().min(1).max(80).nullable(),
    execution_id: nullableBoundedIdentity,
    occurred_at: aiLogTimestamp,
    operation: z.enum(['read', 'inspect', 'rebuild']),
    outcome: z.enum(['hit', 'miss', 'succeeded', 'failed']),
    error_code: z.string().trim().min(1).max(120).nullable(),
    attributes: AiLogDetailAttributesSchema.default({}),
  })
  .strict();

export const AiLogSourceRecordSchema = z.union([
  AiUsageLedgerLogSourceSchema,
  AiModelCallLogSourceSchema,
  AiToolCallLogSourceSchema,
  AiFinalMessageLogSourceSchema,
  AiReservationLogSourceSchema,
  AiChatConfigCacheLogSourceSchema,
]);

export type AiLogSourceRecord = z.output<typeof AiLogSourceRecordSchema>;

type RankedAiLogEvent = {
  identity: string;
  authorityRank: number;
  event: AiLogEvent;
};

export function mapAiLogSourceRecords(records: readonly unknown[]): AiLogEvent[] {
  const sourceRecords = records.map((record) => AiLogSourceRecordSchema.parse(record));
  const scopedModelAttemptAliases = new Map<string, string>();
  for (const record of sourceRecords) {
    if (record.source_kind !== 'ai_usage_ledger') continue;
    scopedModelAttemptAliases.set(
      aiLogIdentity(
        'model_attempt',
        record.tenant_id,
        tenantScopedAttemptId(record.tenant_id, record.attempt_id),
      ),
      aiLogIdentity('model_attempt', record.tenant_id, record.attempt_id),
    );
  }

  const selected = new Map<string, RankedAiLogEvent>();
  for (const record of sourceRecords) {
    const candidate = mapAiLogSourceRecord(record, scopedModelAttemptAliases);
    const current = selected.get(candidate.identity);
    if (!current || candidate.authorityRank > current.authorityRank) {
      selected.set(candidate.identity, candidate);
      continue;
    }
    if (
      candidate.authorityRank === current.authorityRank &&
      JSON.stringify(candidate.event) !== JSON.stringify(current.event)
    ) {
      throw new Error(`AI log source identity conflict: ${candidate.identity}`);
    }
  }
  return [...selected.values()]
    .map(({ event }) => event)
    .sort(
      (left, right) =>
        left.occurred_at.localeCompare(right.occurred_at) ||
        left.event_id.localeCompare(right.event_id),
    );
}

function mapAiLogSourceRecord(
  record: AiLogSourceRecord,
  scopedModelAttemptAliases: ReadonlyMap<string, string>,
): RankedAiLogEvent {
  switch (record.source_kind) {
    case 'ai_usage_ledger':
      return mapUsageLedgerSource(record);
    case 'ai_model_call_events':
      return mapModelCallSource(record, scopedModelAttemptAliases);
    case 'ai_tool_call_events':
      return mapToolCallSource(record);
    case 'ai_chat_messages':
      return mapFinalMessageSource(record);
    case 'ai_point_reservations':
      return mapReservationSource(record);
    case 'chat_config_cache_event':
      return mapCacheSource(record);
  }
}

function mapUsageLedgerSource(
  record: z.output<typeof AiUsageLedgerLogSourceSchema>,
): RankedAiLogEvent {
  const dimensions = parseAiUsageLedgerDimensionsJson(
    record.usage_dimensions_json,
    record.usage_dimensions_schema_version,
  );
  const details = record.details_json
    ? parseAiLogEventDetailsJson(record.details_json)
    : {
        event_type: 'model_attempt' as const,
        schema_version: AI_LOG_SCHEMA_VERSION,
        source_result: record.result,
        internal_call: sourceFlag(record.internal_call),
        attributes: { ledger_source: record.source },
      };
  const event = AiLogEventSchema.parse({
    schema_version: AI_LOG_SCHEMA_VERSION,
    event_id: record.attempt_id,
    event_type: 'model_attempt',
    authority: 'ai_usage_ledger',
    tenant_id: record.tenant_id,
    user_id: record.user_id,
    occurred_at: record.occurred_at,
    execution_id: record.execution_id,
    call_id: record.call_id,
    attempt_number: record.attempt_no,
    action_type: record.action_type,
    action_id: record.action_id,
    message_id: record.message_id,
    reservation_id: record.reservation_id,
    skill_id: record.skill_id,
    tool_call_id: record.tool_call_id,
    tool_name: record.tool_name,
    model: {
      product_model_id: record.model_id,
      runtime_provider: null,
      runtime_model_id: null,
      provider_request_id: record.provider_request_id,
    },
    ...normalizedAttemptResults(record.result),
    error: aiLogError(record.error_category, null, null),
    usage: aiLogUsage(record, dimensions),
    cost: aiLogCost(record),
    quality:
      record.quality_event_id && record.quality_occurred_at
        ? {
            event_id: record.quality_event_id,
            occurred_at: record.quality_occurred_at,
            result: 'failed',
          }
        : null,
    details,
  });
  return {
    identity: aiLogIdentity('model_attempt', record.tenant_id, record.attempt_id),
    authorityRank: 100,
    event,
  };
}

function mapModelCallSource(
  record: z.output<typeof AiModelCallLogSourceSchema>,
  scopedModelAttemptAliases: ReadonlyMap<string, string>,
): RankedAiLogEvent {
  const sourceResult =
    record.result ?? (record.outcome === 'succeeded' ? 'provider_succeeded' : 'provider_failed');
  const event = AiLogEventSchema.parse({
    schema_version: AI_LOG_SCHEMA_VERSION,
    event_id: record.id,
    event_type: 'model_attempt',
    authority: 'ai_model_call_events',
    tenant_id: record.tenant_id,
    user_id: record.user_id,
    occurred_at: record.created_at,
    execution_id: record.execution_id ?? record.ref_id,
    call_id: record.call_id ?? 'legacy',
    attempt_number: record.attempt_no,
    action_type: record.ref_type,
    action_id: record.ref_id,
    message_id: record.ref_type === 'chat_message' ? record.ref_id : null,
    reservation_id: record.reservation_id,
    skill_id: null,
    tool_call_id: null,
    tool_name: null,
    model: {
      product_model_id: record.model_id,
      runtime_provider: null,
      runtime_model_id: null,
      provider_request_id: null,
    },
    ...normalizedAttemptResults(sourceResult),
    error: aiLogError(record.error_category, null, null),
    usage: {
      prompt_tokens: record.prompt_tokens,
      completion_tokens: record.completion_tokens,
      cache_read_tokens: record.cache_read_tokens,
      cache_write_tokens: record.cache_write_tokens,
      reasoning_tokens: record.reasoning_tokens,
      total_tokens: record.total_tokens,
      output_units: null,
      input_tokens: record.prompt_tokens,
      output_tokens: record.completion_tokens,
      cached_tokens: record.cache_read_tokens,
      image_input_units: record.image_input_units,
      image_output_units: record.image_output_units,
      audio_input_units: record.audio_input_units,
      audio_output_units: record.audio_output_units,
      tool_calls: null,
    },
    cost: null,
    quality: null,
    details: {
      event_type: 'model_attempt',
      schema_version: AI_LOG_SCHEMA_VERSION,
      source_result: sourceResult,
      internal_call: sourceFlag(record.internal_call),
      attributes: {},
    },
  });
  const sourceIdentity = aiLogIdentity('model_attempt', record.tenant_id, record.id);
  return {
    identity: scopedModelAttemptAliases.get(sourceIdentity) ?? sourceIdentity,
    authorityRank: 10,
    event,
  };
}

function mapToolCallSource(record: z.output<typeof AiToolCallLogSourceSchema>): RankedAiLogEvent {
  const event = AiLogEventSchema.parse({
    schema_version: AI_LOG_SCHEMA_VERSION,
    event_id: record.event_id,
    event_type: 'tool_call',
    authority: 'ai_tool_call_events',
    tenant_id: record.tenant_id,
    user_id: null,
    occurred_at: record.occurred_at,
    execution_id: record.execution_id,
    call_id: record.tool_call_id,
    attempt_number: record.attempt_number,
    action_type: 'tool_call',
    action_id: record.action_id,
    message_id: record.message_id,
    reservation_id: record.reservation_id,
    skill_id: record.skill_id,
    tool_call_id: record.tool_call_id,
    tool_name: record.tool_name,
    model: null,
    provider_result: null,
    business_result: record.result === 'succeeded' ? 'succeeded' : 'failed',
    error: null,
    usage: null,
    cost:
      record.actual_cost_micros === null
        ? null
        : {
            input_cost_per_1m_cents: null,
            output_cost_per_1m_cents: null,
            actual_cost_micros: record.actual_cost_micros,
            currency: record.currency,
            billing_owner: null,
            fixed_points: null,
          },
    quality: null,
    details: {
      event_type: 'tool_call',
      schema_version: AI_LOG_SCHEMA_VERSION,
      tool_type: record.tool_type,
      source_result: record.result,
      cost_incurred: sourceFlag(record.cost_incurred),
      attributes: {},
    },
  });
  return {
    identity: aiLogIdentity(
      'tool_call',
      record.tenant_id,
      record.reservation_id,
      record.tool_call_id,
      record.attempt_number,
    ),
    authorityRank: 100,
    event,
  };
}

function mapFinalMessageSource(
  record: z.output<typeof AiFinalMessageLogSourceSchema>,
): RankedAiLogEvent {
  const event = AiLogEventSchema.parse({
    schema_version: AI_LOG_SCHEMA_VERSION,
    event_id: record.id,
    event_type: 'final_message',
    authority: 'ai_chat_messages',
    tenant_id: record.tenant_id,
    user_id: record.user_id,
    occurred_at: record.completed_at ?? record.created_at,
    execution_id: record.run_id,
    call_id: null,
    attempt_number: null,
    action_type: 'chat_message',
    action_id: record.run_id,
    message_id: record.id,
    reservation_id: record.reservation_id,
    skill_id: null,
    tool_call_id: null,
    tool_name: null,
    model: record.model_id
      ? {
          product_model_id: record.model_id,
          runtime_provider: null,
          runtime_model_id: null,
          provider_request_id: null,
        }
      : null,
    provider_result: null,
    business_result:
      record.status === 'processing'
        ? 'pending'
        : record.status === 'succeeded'
          ? 'succeeded'
          : 'failed',
    error: aiLogError(null, record.failure_code, record.failure_message),
    usage: {
      prompt_tokens: record.prompt_tokens,
      completion_tokens: record.completion_tokens,
      cache_read_tokens: record.cache_read_tokens,
      cache_write_tokens: record.cache_write_tokens,
      reasoning_tokens: record.reasoning_tokens,
      total_tokens: record.total_tokens,
      output_units: null,
      input_tokens: record.prompt_tokens,
      output_tokens: record.completion_tokens,
      cached_tokens: record.cache_read_tokens,
      image_input_units: null,
      image_output_units: null,
      audio_input_units: null,
      audio_output_units: null,
      tool_calls: null,
    },
    cost: null,
    quality: null,
    details: {
      event_type: 'final_message',
      schema_version: AI_LOG_SCHEMA_VERSION,
      role: record.role,
      status: record.status,
      attributes: {},
    },
  });
  return {
    identity: aiLogIdentity('final_message', record.tenant_id, record.id),
    authorityRank: 100,
    event,
  };
}

function mapReservationSource(
  record: z.output<typeof AiReservationLogSourceSchema>,
): RankedAiLogEvent {
  const failureReason = record.settlement_failure_reason ?? record.refund_reason;
  const event = AiLogEventSchema.parse({
    schema_version: AI_LOG_SCHEMA_VERSION,
    event_id: record.id,
    event_type: 'reservation',
    authority: 'ai_point_reservations',
    tenant_id: record.tenant_id,
    user_id: record.user_id,
    occurred_at: record.refunded_at ?? record.settled_at ?? record.reserved_at,
    execution_id: record.ref_id,
    call_id: null,
    attempt_number: null,
    action_type: record.ref_type,
    action_id: record.ref_id,
    message_id: record.ref_type === 'chat_message' ? record.ref_id : null,
    reservation_id: record.id,
    skill_id: null,
    tool_call_id: null,
    tool_name: null,
    model: record.final_model_id
      ? {
          product_model_id: record.final_model_id,
          runtime_provider: null,
          runtime_model_id: null,
          provider_request_id: null,
        }
      : null,
    provider_result: null,
    business_result: null,
    error: failureReason ? aiLogError('settlement', failureReason, null) : null,
    usage: null,
    cost: null,
    quality: null,
    details: {
      event_type: 'reservation',
      schema_version: AI_LOG_SCHEMA_VERSION,
      status: record.status,
      reserved_points: record.reserved_points,
      settled_points: record.settled_points,
      released_points: record.released_points,
      shortfall_points: record.settlement_shortfall_points,
      failure_reason: failureReason,
      attributes: { feature_key: record.feature_key },
    },
  });
  return {
    identity: aiLogIdentity('reservation', record.tenant_id, record.id),
    authorityRank: 100,
    event,
  };
}

function mapCacheSource(
  record: z.output<typeof AiChatConfigCacheLogSourceSchema>,
): RankedAiLogEvent {
  const event = AiLogEventSchema.parse({
    schema_version: AI_LOG_SCHEMA_VERSION,
    event_id: record.event_id,
    event_type: 'cache_event',
    authority: 'chat_config_cache_event',
    tenant_id: record.tenant_id,
    user_id: record.user_id,
    occurred_at: record.occurred_at,
    execution_id: record.execution_id,
    call_id: null,
    attempt_number: null,
    action_type: 'chat_config_cache',
    action_id: record.event_id,
    message_id: null,
    reservation_id: null,
    skill_id: null,
    tool_call_id: null,
    tool_name: null,
    model: null,
    provider_result: null,
    business_result: record.outcome === 'failed' ? 'failed' : 'succeeded',
    error: record.error_code ? aiLogError('cache', record.error_code, null) : null,
    usage: null,
    cost: null,
    quality: null,
    details: {
      event_type: 'cache_event',
      schema_version: AI_LOG_SCHEMA_VERSION,
      cache_name: 'chat_config',
      operation: record.operation,
      outcome: record.outcome,
      attributes: record.attributes,
    },
  });
  return {
    identity: aiLogIdentity('cache_event', record.tenant_id, record.event_id),
    authorityRank: 100,
    event,
  };
}

function normalizedAttemptResults(
  result: z.output<typeof AiLogModelAttemptDetailsSchema>['source_result'],
): {
  provider_result: 'succeeded' | 'failed';
  business_result: 'succeeded' | 'failed' | null;
} {
  if (result === 'provider_failed') {
    return { provider_result: 'failed', business_result: null };
  }
  return {
    provider_result: 'succeeded',
    business_result:
      result === 'output_validation_failed' || result === 'business_validation_failed'
        ? 'failed'
        : 'succeeded',
  };
}

function aiLogUsage(
  record: z.output<typeof AiUsageLedgerLogSourceSchema>,
  dimensions: AiUsageLedgerDimensions,
): z.output<typeof AiLogUsageSchema> {
  return {
    prompt_tokens: record.prompt_tokens,
    completion_tokens: record.completion_tokens,
    cache_read_tokens: record.cache_read_tokens,
    cache_write_tokens: record.cache_write_tokens,
    reasoning_tokens: record.reasoning_tokens ?? dimensions.reasoning_tokens,
    total_tokens: record.total_tokens ?? dimensions.total_tokens,
    output_units: dimensions.output_units,
    input_tokens: dimensions.input_tokens,
    output_tokens: dimensions.output_tokens,
    cached_tokens: dimensions.cached_tokens,
    image_input_units: dimensions.image_input_units,
    image_output_units: dimensions.image_output_units,
    audio_input_units: dimensions.audio_input_units,
    audio_output_units: dimensions.audio_output_units,
    tool_calls: dimensions.tool_calls,
  };
}

function aiLogCost(
  record: z.output<typeof AiUsageLedgerLogSourceSchema>,
): z.output<typeof AiLogCostSchema> | null {
  const cost = {
    input_cost_per_1m_cents: record.input_cost_per_1m_cents,
    output_cost_per_1m_cents: record.output_cost_per_1m_cents,
    actual_cost_micros: record.actual_cost_micros,
    currency: record.currency,
    billing_owner: record.billing_owner,
    fixed_points: record.fixed_points,
  };
  return Object.values(cost).every((value) => value === null) ? null : AiLogCostSchema.parse(cost);
}

function aiLogError(
  category: string | null,
  code: string | null,
  message: string | null,
): AiLogEvent['error'] {
  return category === null && code === null && message === null
    ? null
    : { category, code, message };
}

function sourceFlag(value: boolean | 0 | 1): boolean {
  return value === true || value === 1;
}

function normalizeAiLogDetailKey(key: string): string {
  return key
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

function stableHash32(value: string, seed: number): number {
  let hash = seed >>> 0;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash;
}

function aiLogIdentity(
  eventType: string,
  tenantId: string,
  ...parts: Array<string | number>
): string {
  return [eventType, tenantId, ...parts].join(':');
}
